import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { AppShell } from "@/components/layout/AppShell";
import { RouteGuard } from "@/components/layout/RouteGuard";
import MCPsPage from "@/features/mcps";

/**
 * MCPs — the first screen migrated off the legacy react-router tree.
 *
 * The sidebar labels this "Connectors" per the design, but MCP is the domain
 * term the code, the URL and the i18n namespace all use, so the folder follows
 * that rather than the label.
 *
 * It was the natural first candidate: 224 lines, zero MUI imports, zero
 * react-router hooks anywhere in its subtree, zero useEffect data fetching, and
 * already on react-query. The only thing tying it to the old world was the layout
 * it rendered.
 *
 * The layout now belongs to the ROUTE rather than the page, which is why
 * index.tsx no longer renders a layout of its own. That is the shape every
 * migrated screen should follow.
 *
 * contentWidth="full" here overrides AppShell's default, and it is the one place
 * so far where the design beats legacy parity: the legacy call had no `fullHeight`
 * so the content was capped at 1536px, but the redesign makes this screen a
 * two-pane split that runs edge to edge. "contained" remains right for the other
 * screens migrated by parity alone.
 *
 * The two titles are not a duplicate. The top bar carries the nav label
 * ("Connectors"), the list column carries the domain term ("MCPs") — which is
 * exactly how the design has it, and the same distinction that decided this
 * folder's name.
 *
 * The corresponding <Route path="/mcps"> has been deleted from legacy-routes.jsx.
 * Leaving it in place would be harmless but misleading — this route takes
 * precedence over the catch-all, so the legacy entry would be dead code.
 */
const MCPsRoute = () => {
  const { t } = useTranslation();
  return (
    // Mirrors the legacy route exactly: <ProtectedRoute><HideInferenceOnlyRoute>.
    // Dropping this would let an inference-only org reach a page it must not.
    <RouteGuard allow={(p) => !p.isInferenceOnly}>
      <AppShell title={t("sidebar.connectors")} contentWidth="full">
        <MCPsPage />
      </AppShell>
    </RouteGuard>
  );
};

export const Route = createFileRoute("/mcps")({
  component: MCPsRoute,
});
